import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { parse, startOfDay } from 'date-fns';
import { toast } from 'react-toastify';
import { toZonedTime } from 'date-fns-tz';
import { useDispatch } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormInput from '../../../components/form/FormInput';

import { useTimezone } from '../../../store/slices/commonSlice';
import { timezoneAdd, timezoneUpdate } from '../../../store/slices/adminSlices/timezoneSlice';
import FormDateInput from '../../../components/form/FormDateInput';
import FormToggle from '../../../components/form/FormToggle';
import { formatPayloadTime } from '../../../utils/common';

const AddEditTimezoneModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();
  const timezone = useTimezone();
  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    zone_name: '',
    abbrev: '',
    utc_offset: startOfDay(today),
    is_dst: false
  };

  const validationSchema = Yup.object({
    zone_name: Yup.string().required('zone name is required'),
    abbrev: Yup.string().required('abbrev is required'),
    utc_offset: Yup.string().required('utc offset is required')
  });

  const onSubmit = async () => {
    if (isEdit) {
      try {
        const payload = { ...values, id: editRecord.id };
        payload['utc_offset'] = formatPayloadTime(values.utc_offset);
        const response = await dispatch(timezoneUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = { ...values };
        payload['utc_offset'] = formatPayloadTime(values.utc_offset);
        const response = await dispatch(timezoneAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  useEffect(() => {
    if (isEdit) {
      const utc_offset_time = `${Math.abs(Number(editRecord?.utc_offset?.hours || 0))}:${Math.abs(Number(editRecord?.utc_offset?.minutes || 0))}`;
      setValues({
        ...editRecord,
        utc_offset: parse(utc_offset_time, 'HH:mm', today)
      });
    }
  }, [editRecord]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Timezone' : 'Add Timezone'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="timezoneForm" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="zone_name"
                label="Zone Name"
                placeholder="Enter Zone Name"
                value={values.zone_name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="abbrev"
                label="Abbrev"
                placeholder="Enter Abbrev"
                value={values.abbrev}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormDateInput
                label="UTC offset"
                dateFormat="HH:mm"
                timeFormat="HH:mm"
                showTimeSelect={true}
                name="utc_offset"
                timeIntervals="1"
                value={values.utc_offset}
                showTimeSelectOnly={true}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="is_dst"
                label="IsDst"
                value={String(values.is_dst === 'true')}
                onChange={() =>
                  setFieldValue('is_dst', values.is_dst === 'true' ? 'false' : 'true')
                }
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="timezoneForm" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditTimezoneModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditTimezoneModal;
